if (!customElements.get('investment-performance-chart')) {
  class InvestmentPerformanceChart extends HTMLElement {
    constructor() {
      super();
      this.instanceId = this.dataset.instanceId || 'investment-performance-chart-' + Math.random().toString(36).slice(2, 10);
      this.shadowId = this.instanceId + '-shadow';
    }

    connectedCallback() {
      this.render();
      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
      window.requestAnimationFrame(() => this.render());
    };

    render() {
      var startYear = parseInt(this.dataset.startYear, 10);
      var endYear = parseInt(this.dataset.endYear, 10);
      var wineCagr = parseFloat(this.dataset.wineCagr || '10.4') / 100;
      var growthTotal = parseFloat(this.dataset.growthTotal || '146') / 100;
      var outperformance = parseFloat(this.dataset.outperformance || '50') / 100;
      var wineLabel = this.dataset.wineLabel || 'Fine Wine';
      var benchmarkLabel = this.dataset.benchmarkLabel || 'Vergleichsindex';
      var description = this.dataset.chartDescription || '';

      if (Number.isNaN(startYear) || Number.isNaN(endYear) || startYear >= endYear) {
        return;
      }

      var years = [];
      for (var year = startYear; year <= endYear; year += 1) {
        years.push(year);
      }

      var wineEndIndex = 100 * (1 + growthTotal);
      var benchmarkEndIndex = 100 + growthTotal / (1 + outperformance) * 100;
      var benchmarkCagr = Math.pow(benchmarkEndIndex / 100, 1 / 10) - 1;
      var wineSeries = [];
      var benchmarkSeries = [];
      var wineValue = 100;
      var benchmarkValue = 100;
      var benchmarkShockMap = {
        2008: -0.14,
        2009: 0.08,
        2011: -0.06,
        2012: 0.03,
        2018: -0.04,
        2020: -0.08,
        2021: 0.11,
        2022: -0.12,
        2023: 0.06
      };
      var wineShockMap = {
        2008: -0.03,
        2009: 0.05,
        2011: 0.01,
        2018: -0.01,
        2020: -0.02,
        2021: 0.06,
        2022: 0.02,
        2023: 0.03
      };

      years.forEach(function (pointYear, index) {
        if (index === 0) {
          wineSeries.push(100);
          benchmarkSeries.push(100);
          return;
        }

        wineValue = wineValue * (1 + wineCagr + (wineShockMap[pointYear] || 0));
        benchmarkValue = benchmarkValue * (1 + benchmarkCagr + (benchmarkShockMap[pointYear] || 0));

        wineSeries.push(wineValue);
        benchmarkSeries.push(benchmarkValue);
      });

      var wineFinalScale = (wineEndIndex - 100) / (wineSeries[wineSeries.length - 1] - 100);
      wineSeries = wineSeries.map(function (value) {
        return 100 + (value - 100) * wineFinalScale;
      });

      var benchmarkFinalScale = (benchmarkEndIndex - 100) / (benchmarkSeries[benchmarkSeries.length - 1] - 100);
      benchmarkSeries = benchmarkSeries.map(function (value) {
        return 100 + (value - 100) * benchmarkFinalScale;
      });

      var allValues = wineSeries.concat(benchmarkSeries, [100]);
      var maxValue = Math.max.apply(null, allValues);
      var minValue = Math.min.apply(null, allValues);
      var roundedMax = Math.ceil(maxValue / 50) * 50;
      var roundedMin = Math.floor(minValue / 50) * 50;
      var yTicks = [];
      for (var tick = roundedMin; tick <= roundedMax; tick += 50) {
        yTicks.push(tick);
      }

      var width = 720;
      var height = 430;
      var padding = { top: 24, right: 18, bottom: 42, left: 52 };
      var innerWidth = width - padding.left - padding.right;
      var innerHeight = height - padding.top - padding.bottom;
      var xStep = years.length > 1 ? innerWidth / (years.length - 1) : innerWidth;

      var scaleX = function (index) {
        return padding.left + xStep * index;
      };

      var scaleY = function (value) {
        return padding.top + innerHeight - ((value - roundedMin) / (roundedMax - roundedMin)) * innerHeight;
      };

      var makePath = function (series) {
        return series.map(function (value, index) {
          var command = index === 0 ? 'M' : 'L';
          return command + scaleX(index).toFixed(2) + ' ' + scaleY(value).toFixed(2);
        }).join(' ');
      };

      var yGrid = yTicks.map(function (tickValue) {
        var y = scaleY(tickValue).toFixed(2);
        return '<line x1="' + padding.left + '" y1="' + y + '" x2="' + (width - padding.right) + '" y2="' + y + '" class="investment-performance-chart__grid" />' +
          '<text x="' + (padding.left - 12) + '" y="' + (parseFloat(y) + 4) + '" text-anchor="end" class="investment-performance-chart__tick">' + tickValue + '</text>';
      }).join('');

      var xLabels = years.map(function (tickYear, index) {
        if (index !== 0 && index !== years.length - 1 && index % 4 !== 0) {
          return '';
        }
        var x = scaleX(index).toFixed(2);
        return '<text x="' + x + '" y="' + (height - 10) + '" text-anchor="middle" class="investment-performance-chart__tick">' + tickYear + '</text>';
      }).join('');

      var wineEnd = wineSeries[wineSeries.length - 1];
      var benchmarkEnd = benchmarkSeries[benchmarkSeries.length - 1];
      var pointMarkers = years.map(function (_, index) {
        return '<circle class="investment-performance-chart__point" data-point-index="' + index + '" cx="' + scaleX(index).toFixed(2) + '" cy="' + scaleY(wineSeries[index]).toFixed(2) + '" r="4" />' +
          '<circle class="investment-performance-chart__point investment-performance-chart__point--benchmark" data-point-index="' + index + '" cx="' + scaleX(index).toFixed(2) + '" cy="' + scaleY(benchmarkSeries[index]).toFixed(2) + '" r="3" />';
      }).join('');

      this.innerHTML =
        '<svg viewBox="0 0 ' + width + ' ' + height + '" role="img" aria-label="' + this.escape(description) + '">' +
          '<defs>' +
            '<filter id="' + this.shadowId + '" x="-20%" y="-20%" width="140%" height="140%">' +
              '<feDropShadow dx="0" dy="8" stdDeviation="10" flood-opacity="0.12" />' +
            '</filter>' +
          '</defs>' +
          '<rect x="' + padding.left + '" y="' + padding.top + '" width="' + innerWidth + '" height="' + innerHeight + '" rx="18" fill="var(--investment-performance-fill-soft)" />' +
          yGrid +
          '<path d="' + makePath(benchmarkSeries) + '" fill="none" stroke="var(--investment-performance-line-benchmark)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" filter="url(#' + this.shadowId + ')" />' +
          '<path d="' + makePath(wineSeries) + '" fill="none" stroke="var(--investment-performance-line-wine)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" filter="url(#' + this.shadowId + ')" />' +
          pointMarkers +
          '<circle cx="' + scaleX(years.length - 1).toFixed(2) + '" cy="' + scaleY(wineEnd).toFixed(2) + '" r="5" fill="var(--investment-performance-line-wine)" />' +
          '<circle cx="' + scaleX(years.length - 1).toFixed(2) + '" cy="' + scaleY(benchmarkEnd).toFixed(2) + '" r="5" fill="var(--investment-performance-line-benchmark)" />' +
          '<text x="' + (scaleX(years.length - 1) - 8).toFixed(2) + '" y="' + (scaleY(wineEnd) - 12).toFixed(2) + '" text-anchor="end" class="investment-performance-chart__label">' + this.escape(wineLabel) + '</text>' +
          '<text x="' + (scaleX(years.length - 1) - 8).toFixed(2) + '" y="' + (scaleY(benchmarkEnd) + 20).toFixed(2) + '" text-anchor="end" class="investment-performance-chart__label investment-performance-chart__label--soft">' + this.escape(benchmarkLabel) + '</text>' +
          xLabels +
          '<line class="investment-performance-chart__crosshair" x1="0" y1="' + padding.top + '" x2="0" y2="' + (height - padding.bottom) + '" style="opacity:0;" />' +
          '<rect class="investment-performance-chart__hover-layer" x="' + padding.left + '" y="' + padding.top + '" width="' + innerWidth + '" height="' + innerHeight + '" />' +
        '</svg>' +
        '<div class="investment-performance-chart__tooltip" role="status" aria-live="polite"></div>';

      this.wireUpInteractivity({
        svg: this.querySelector('svg'),
        crosshair: this.querySelector('.investment-performance-chart__crosshair'),
        hoverLayer: this.querySelector('.investment-performance-chart__hover-layer'),
        tooltip: this.querySelector('.investment-performance-chart__tooltip'),
        points: this.querySelectorAll('.investment-performance-chart__point'),
        years: years,
        wineSeries: wineSeries,
        benchmarkSeries: benchmarkSeries,
        scaleX: scaleX,
        padding: padding,
        width: width,
        wineLabel: wineLabel,
        benchmarkLabel: benchmarkLabel
      });
    }

    escape(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    wireUpInteractivity(chart) {
      var self = this;

      var pointStep = chart.years.length > 1 ? chart.scaleX(1) - chart.scaleX(0) : 0;

      var pointFromClientX = function (clientX) {
        var rect = chart.svg.getBoundingClientRect();
        if (!rect.width) return null;
        if (pointStep === 0) return 0;
        var scale = chart.width / rect.width;
        var svgX = (clientX - rect.left) * scale;
        var relativeX = svgX - chart.padding.left;
        var index = Math.round(relativeX / pointStep);
        return Math.max(0, Math.min(chart.years.length - 1, index));
      };

      var showTooltipForIndex = function (index) {
        var wrapperRect = self.getBoundingClientRect();
        var svgRect = chart.svg.getBoundingClientRect();
        var x = chart.scaleX(index) / chart.width * wrapperRect.width;
        var y = svgRect.top - wrapperRect.top;

        chart.tooltip.innerHTML =
          '<div class="investment-performance-chart__tooltip-date">' + chart.years[index] + '</div>' +
          '<div class="investment-performance-chart__tooltip-row"><span>' + self.escape(chart.wineLabel) + '</span><span>Index ' + Math.round(chart.wineSeries[index]) + '</span></div>' +
          '<div class="investment-performance-chart__tooltip-row"><span>' + self.escape(chart.benchmarkLabel) + '</span><span>Index ' + Math.round(chart.benchmarkSeries[index]) + '</span></div>';
        chart.tooltip.style.left = x + 'px';
        chart.tooltip.style.top = y + 'px';
        chart.tooltip.classList.add('is-visible');

        var crosshairX = chart.scaleX(index);
        chart.crosshair.setAttribute('x1', crosshairX);
        chart.crosshair.setAttribute('x2', crosshairX);
        chart.crosshair.style.opacity = '1';

        chart.points.forEach(function (point) {
          point.classList.toggle('is-active', parseInt(point.dataset.pointIndex, 10) === index);
        });
      };

      var hideTooltip = function () {
        chart.tooltip.classList.remove('is-visible');
        chart.crosshair.style.opacity = '0';
        chart.points.forEach(function (point) {
          point.classList.remove('is-active');
        });
      };

      if (chart.hoverLayer) {
        chart.hoverLayer.addEventListener('mousemove', function (evt) {
          var index = pointFromClientX(evt.clientX);
          if (index === null) return;
          showTooltipForIndex(index);
        });
        chart.hoverLayer.addEventListener('mouseleave', hideTooltip);
        chart.hoverLayer.addEventListener('touchstart', function (evt) {
          if (!evt.touches || !evt.touches[0]) return;
          var index = pointFromClientX(evt.touches[0].clientX);
          if (index === null) return;
          showTooltipForIndex(index);
        }, { passive: true });
        chart.hoverLayer.addEventListener('touchend', hideTooltip);
      }

      chart.points.forEach(function (point) {
        point.addEventListener('mouseenter', function () {
          var index = parseInt(point.dataset.pointIndex, 10);
          if (Number.isNaN(index)) return;
          showTooltipForIndex(index);
        });
        point.addEventListener('mouseleave', hideTooltip);
      });
    }
  }

  customElements.define('investment-performance-chart', InvestmentPerformanceChart);
}
